import { Head, useForm } from "@inertiajs/react"

import { CategorySelect } from "@/components/category-select"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { editEntryPath, entriesPath, entryPath } from "@/routes"
import type { BreadcrumbItem, Category, Entry } from "@/types"

interface EditEntryProps {
  entry: Entry
  categories: Category[]
}

export default function EditEntry({ entry, categories }: EditEntryProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Entries", href: entriesPath() },
    { title: "Edit Entry", href: editEntryPath({ id: entry.id }) },
  ]

  const form = useForm({
    description: entry.description,
    amount: String(entry.amount),
    date: entry.date,
    direction: entry.direction,
    category_id: entry.category ? String(entry.category.id) : "",
    tag_names: entry.tags.join(", "),
  })
  const amountError = (form.errors as Partial<Record<"amount_cents", string>>)
    .amount_cents

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.patch(entryPath({ id: entry.id }))
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Entry" />

      <div className="mx-auto w-full max-w-lg space-y-6 p-4">
        <h2 className="text-lg font-semibold">Edit Entry</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={form.data.description}
              onChange={(e) => form.setData("description", e.target.value)}
              autoFocus
            />
            <InputError messages={form.errors.description} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={form.data.amount}
              onChange={(e) => form.setData("amount", e.target.value)}
            />
            <InputError messages={amountError ? [amountError] : undefined} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={form.data.date}
              onChange={(e) => form.setData("date", e.target.value)}
            />
            <InputError messages={form.errors.date} />
          </div>

          <div className="grid gap-2">
            <Label>Type</Label>
            <div className="flex gap-2">
              {(["expense", "revenue", "transfer", "tax"] as const).map(
                (dir) => (
                  <Button
                    key={dir}
                    type="button"
                    variant={
                      form.data.direction === dir ? "default" : "outline"
                    }
                    onClick={() => form.setData("direction", dir)}
                    className="flex-1"
                  >
                    {dir === "expense"
                      ? "Expense"
                      : dir === "revenue"
                        ? "Revenue"
                        : dir === "transfer"
                          ? "Transfer"
                          : "Tax"}
                  </Button>
                ),
              )}
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="category">Category (optional)</Label>
            <CategorySelect
              value={form.data.category_id}
              onValueChange={(value) => form.setData("category_id", value)}
              categories={categories}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="tag_names">Tags (optional)</Label>
            <Input
              id="tag_names"
              value={form.data.tag_names}
              onChange={(e) => form.setData("tag_names", e.target.value)}
              placeholder="e.g. Tanooki, Payroll"
            />
            <p className="text-muted-foreground text-xs">
              Separate multiple tags with commas.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={form.processing}>
              Save
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
